import { HTMLAttributes, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';
import { RichTextStatic } from '@legacy/components/ui-components/RichText';
import { useLocalStorage } from '@legacy/hooks/useLocalStorage';
import { Button, Stack, Typography } from '@legacy/components/mui-components';
import styles from './StaticText.module.scss';
import { internalMessageExpandLocalStorageKey } from './localStorageKeys';

interface StaticTextProps extends HTMLAttributes<HTMLDivElement> {
  isEmpty: boolean;
  standardText: string;
  internalMessage: string;
}

export const StaticText = ({
  isEmpty,
  standardText,
  internalMessage,
  className,
  ...props
}: StaticTextProps) => {
  const [showAll, setShowAll] = useLocalStorage(
    internalMessageExpandLocalStorageKey,
    false,
  );
  const [hasOverflow, setHasOverflow] = useState(false);
  const richTextRef = useRef<HTMLDivElement>(null);
  const { t } = useTranslation('frontpage');

  useEffect(() => {
    if (richTextRef.current) {
      if (
        richTextRef.current?.scrollHeight >
        parseInt(styles.richTextMaxHeight, 10)
      ) {
        setHasOverflow(true);
      } else {
        setHasOverflow(false);
      }
    }
  }, [internalMessage, richTextRef, hasOverflow]);

  return isEmpty ? (
    <Typography data-automation-id="InternalMessageEmptyText">
      {standardText}
    </Typography>
  ) : (
    <>
      <div
        ref={richTextRef}
        id="InternalMessageTextStatic"
        className={cx(
          styles.staticText,
          { [styles.showAll]: showAll },
          className,
        )}
      >
        <RichTextStatic
          html={internalMessage}
          data-automation-id="InternalMessageTextStatic"
          {...props}
        />
      </div>
      {hasOverflow && (
        <Stack direction="row" justifyContent="right">
          <Button
            variant="outlined"
            data-automation-id="InternalMessageExtendButton"
            size="small"
            onClick={() => setShowAll((prev: boolean) => !prev)}
            aria-controls="InternalMessageTextStatic"
            aria-expanded={showAll}
          >
            {showAll
              ? t('InternalMessageWidgetCollapseContentButtonText')
              : t('InternalMessageWidgetExpandContentButtonText')}
          </Button>
        </Stack>
      )}
    </>
  );
};
